<script setup lang="ts">
import { computed, onMounted } from 'vue';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaPageHeader from '@/components/design/composite/GaiaPageHeader.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import ReviewProjectBreadcrumb from '@/components/reviewProject/ReviewProjectBreadcrumb.vue';
import ReviewProjectPageMenu from '@/components/reviewProject/ReviewProjectPageMenu.vue';
import { useReviewProject } from '@/composables/useReviewProject';

/**
 * Shared chrome for a review project's sub pages — statistics, tasks and
 * settings. Each is its own route reached from the gear menu beside the
 * project title, so all three need the same breadcrumb, heading and menu; the
 * project itself is fetched once here and handed to the page through the slot.
 */
const props = withDefaults(
  defineProps<{
    projectUid: string;
    /** The sub page's name — last breadcrumb crumb and the page heading. */
    title: string;
    contentMaxWidth?: string;
  }>(),
  { contentMaxWidth: gaiaContentWidth.wide }
);

const { project, loading, error, fetchProject, patchProject } = useReviewProject(
  () => props.projectUid
);

const heading = computed(() => `${project.value?.name ?? 'Review project'} · ${props.title}`);

onMounted(() => {
  void fetchProject();
});
</script>

<template>
  <Layout :content-max-width="props.contentMaxWidth">
    <template #breadcrumb>
      <review-project-breadcrumb
        :project-uid="props.projectUid"
        :project-name="project?.name"
        :page="props.title"
      />
    </template>

    <n-flex vertical :size="18">
      <gaia-page-header :title="heading">
        <template #actions>
          <review-project-page-menu :project-uid="props.projectUid" />
        </template>
      </gaia-page-header>

      <gaia-banner v-if="error" type="error" title="Couldn’t load the review project">
        {{ error }}
      </gaia-banner>

      <gaia-spinner v-else-if="loading && !project" style="margin-top: 32px" />

      <slot v-else-if="project" :project="project" :patch="patchProject" />
    </n-flex>
  </Layout>
</template>
